import { useState } from 'react';
import { FileDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

export interface ExportOptions {
  includeConversationContent: boolean;
  includeRawData: boolean;
}

interface ExportOptionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (options: ExportOptions) => void;
  isExporting?: boolean;
}

export function ExportOptionsDialog({
  open,
  onOpenChange,
  onConfirm,
  isExporting = false,
}: ExportOptionsDialogProps) {
  const [includeConversationContent, setIncludeConversationContent] = useState(true);
  const [includeRawData, setIncludeRawData] = useState(false);

  const handleConfirm = () => {
    onConfirm({
      includeConversationContent,
      includeRawData,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Export Conversations</DialogTitle>
          <DialogDescription>
            Choose what data to include in your CSV export. All metadata (title, category, timestamps, etc.) will
            always be included.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex items-start space-x-3">
            <Checkbox
              id="conversation-content"
              checked={includeConversationContent}
              onCheckedChange={(checked) => { setIncludeConversationContent(checked === true); }}
            />
            <div className="space-y-1 leading-none">
              <Label htmlFor="conversation-content" className="text-sm font-medium cursor-pointer">
                Include Conversation Content
              </Label>
              <p className="text-sm text-muted-foreground">
                Human-readable formatted conversation with all turns (USER/AGENT messages)
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              id="raw-data"
              checked={includeRawData}
              onCheckedChange={(checked) => { setIncludeRawData(checked === true); }}
            />
            <div className="space-y-1 leading-none">
              <Label htmlFor="raw-data" className="text-sm font-medium cursor-pointer">
                Include Raw S3 Data
              </Label>
              <p className="text-sm text-muted-foreground">
                Complete raw JSON from S3 with all internal structures, tool calls, and metadata
              </p>
            </div>
          </div>

          {!includeConversationContent && !includeRawData && (
            <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
              Only conversation metadata will be exported (no message content or raw data)
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); }} disabled={isExporting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isExporting}>
            <FileDown className="mr-2 h-4 w-4" />
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
